import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '../ui';
import { ErrorState } from './ErrorFallback';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error);
      console.error('Error info:', errorInfo);
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Report error to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      this.reportError(error, errorInfo);
    }
  }

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    // In a real application, you would send this to your error reporting service
    // For now, we'll just log it
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    console.error('Error report:', errorReport);
    
    // TODO: Send to error reporting service
    // errorReportingService.report(errorReport);
  };

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const showDetails = process.env.NODE_ENV === 'development' && this.state.error;

      return (
        <div className="flex min-h-screen items-center justify-center bg-white">
          <ErrorState
            tone="error"
            title="Something went wrong"
            message="Please try again."
            details={showDetails ? `${this.state.error!.message}\n\n${this.state.error!.stack ?? ''}` : undefined}
            actions={
              <>
                <Button variant="secondary" onClick={() => window.location.reload()}>
                  Reload
                </Button>
                <Button variant="dark" onClick={this.handleRetry}>
                  Retry
                </Button>
              </>
            }
          />
        </div>
      );
    }

    return this.props.children;
  }
}